import * as yup from 'yup';
import { IProfession } from '../../models';

export const BriefSteps = {
  VIDEO_CONTENT: 1,
  EXPERT_QUESTIONS: 2,
} as const;

export interface IBriefPdpUrlEntry {
  desireid: number | null;
  url: string;
  desires_pdp_url_id?: number | null;
}

export interface IVideoContent {
  /** Mirrors pdp_urls[0] — the project's primary product. */
  url: string;
  desires_pdp_url_id?: number | null;
  /** One entry per product on the project. */
  pdp_urls?: IBriefPdpUrlEntry[];
  name: string;
  short_name?: string;
  description?: string;
  location_request?: string;
  brand_requests?: string;
  /** TDC-1722 brand/product assets — the brief's snapshot (see IBriefBrandAssets). */
  brief_logo?: string;
  brief_colors?: string;
  brief_colors_file?: string;
  brief_font?: string;
  brief_font_file?: string;
  brief_tagline?: string;
  /** The retail media network's set on an RMN order's brief. */
  rmn_brief_logo?: string;
  rmn_brief_colors?: string;
  rmn_brief_colors_file?: string;
  rmn_brief_font?: string;
  rmn_brief_font_file?: string;
  rmn_brief_tagline?: string;
  product_disclaimers?: Record<string, string>;
}

/**
 * TDC-1722: the brand's brief assets + each product's disclaimers. The brand and
 * product hold the source values; a brief keeps its own snapshot (null = never
 * set) and saving it writes changed values back. File fields are upload URLs.
 */
export interface IBriefBrandAssets {
  brief_logo: string;
  brief_colors: string;
  brief_colors_file: string;
  brief_font: string;
  brief_font_file: string;
  brief_tagline: string;
}

export const BRIEF_BRAND_ASSET_KEYS: (keyof IBriefBrandAssets)[] = [
  'brief_logo',
  'brief_colors',
  'brief_colors_file',
  'brief_font',
  'brief_font_file',
  'brief_tagline',
];

/** The RMN's set on an RMN order's brief: the same fields, `rmn_`-prefixed. */
export const BRIEF_RMN_ASSET_KEYS = BRIEF_BRAND_ASSET_KEYS.map((key) => `rmn_${key}`);
export const BRIEF_ALL_ASSET_KEYS: string[] = [...BRIEF_BRAND_ASSET_KEYS, ...BRIEF_RMN_ASSET_KEYS];

/** GET /brand-projects/:id/brief-assets (and the public token variants). */
export interface IBriefAssetDefaults {
  brand_id: number | null;
  /** The supplier brand (brief_*). */
  brand: { id: number; name: string } | null;
  /** The retail media network (rmn_brief_*); null on a plain brand project. */
  rmn_brand: { id: number; name: string } | null;
  assets: Record<string, string> & { product_disclaimers: Record<string, string> };
  products: { desireid: number; title: string }[];
}

export interface IBriefProfession {
  profession_id: number;
  sub_profession_id?: number;
  order?: number;
}

export interface IExpertQuestions {
  expert_gender?: string;
  ageRange: any[];
  professions?: IBriefProfession[];
  /** 'industry' | 'use_case' | 'either' — the brand's expert-type intent. */
  expert_type?: string;
  expert_interests?: string;
  other?: string;
  special_requests?: string;
  status?: any;
}

// Values match brand_projects_briefs.expert_type; consumed as a soft default
// by the Expert Proposals pool filter.
export const briefExpertTypeOptions = [
  { value: 'either', label: 'Either / No Preference' },
  { value: 'industry', label: 'Industry Expert — a credentialed professional in the field' },
  { value: 'use_case', label: 'Use-Case Expert — a credible practitioner who uses it in real life' },
];

export const initVideoContent = {
  url: '',
  desires_pdp_url_id: null as number | null,
  pdp_urls: [] as IBriefPdpUrlEntry[],
  name: '',
  short_name: '',
  description: '',
  location_request: '',
  brand_requests: '',
  brief_logo: '',
  brief_colors: '',
  brief_colors_file: '',
  brief_font: '',
  brief_font_file: '',
  brief_tagline: '',
  rmn_brief_logo: '',
  rmn_brief_colors: '',
  rmn_brief_colors_file: '',
  rmn_brief_font: '',
  rmn_brief_font_file: '',
  rmn_brief_tagline: '',
  product_disclaimers: {} as Record<string, string>,
};

export const initExpertQuestions = {
  expert_gender: '',
  ageRange: [22, 42],
  professions: [],
  expert_type: 'either',
  expert_interests: '',
  other: '',
  special_requests: '',
};

export const AgeSelector = {
  minDiff: 1,
  minAge: 10,
  maxAge: 100,
};

/** Prefixes a bare host with https:// — the same normalization the API does. */
export const normalizePdpUrl = (raw?: string | null) => {
  const value = (raw || '').trim();
  if (!value || /^https?:\/\//i.test(value)) return value;
  return `https://${value.replace(/^\/+/, '')}`;
};

// Mirrors assertValidUrl in the API (lib/briefPdpUrl.js): a brief URL becomes a
// desires_pdp_urls row that gets fetched and scored, so junk like "n/a" must be
// caught here rather than bounced back as a 400. Empty passes — the PDP link is
// optional, it just has to be a real URL when given.
export const isValidPdpUrl = (raw?: string | null) => {
  const value = (raw || '').trim();
  if (!value) return true;
  try {
    const parsed = new URL(normalizePdpUrl(value));
    if (!/^https?:$/i.test(parsed.protocol)) return false;
    const host = parsed.hostname;
    return host.includes('.') && !host.startsWith('.') && !host.endsWith('.');
  } catch (e) {
    return false;
  }
};

export const PDP_URL_ERROR =
  'Please enter a valid URL (e.g. https://example.com/product)';

const pdpUrlField = () =>
  yup.string().test('valid-pdp-url', PDP_URL_ERROR, (value) =>
    isValidPdpUrl(value)
  );

export const videoContentSchema = yup.object().shape({
  url: pdpUrlField(),
  pdp_urls: yup.array().of(
    yup.object().shape({
      url: pdpUrlField(),
    })
  ),
  name: yup.string()
    .required('Name is required')
    .max(10000, 'Product name and pronunciation must be less than 10,000 characters'),
  short_name: yup.string()
    .required('Short name is required')
    .max(10000, 'Shortened product names must be less than 10,000 characters'),
  description: yup.string().required('Description is required'),
  location_request: yup.string(),
  brand_requests: yup.string(),
});

export const expertQuestionSchema = yup.object().shape({
  expert_gender: yup.string().required('Gender is required'),
  ageRange: yup.array().min(2, 'Age range is required').required('Age range is required'),
  professions: yup.array().min(1, 'At least one profession is required'),
  expert_interests: yup.string().required('Interests are required'),
  other: yup.string().required('Other requirements are required'),
  special_requests: yup.string().required('Filming appearance requests are required'),
});

export const getOptionsArr = (arr: Array<IProfession>) =>
  arr.map((e) => ({
    value: e.id,
    label: e.name,
  }));

export interface BriefPdpUrl {
  id: number
  pdp_url: string
  retailer?: string | null
  active: boolean
}

/** A brief's saved link for one product on its project. */
export interface BriefPdpUrlLink {
  desireid: number
  url: string
  desires_pdp_url_id?: number | null
  /** Flattened by the public serializer... */
  product_title?: string | null
  /** ...nested by the authenticated read. Use briefPdpUrlRows, not either. */
  product?: { desireid: number; title: string } | null
  pdpUrl?: BriefPdpUrl | null
}

export interface Brief {
  created_at: string
  updated_at: string
  short_name?: string
  id: number
  url: string
  desires_pdp_url_id?: number | null
  pdpUrl?: BriefPdpUrl | null
  /** Public share payload spells it snake_case... */
  pdp_urls?: BriefPdpUrlLink[]
  /** ...while the authenticated read returns the Sequelize alias. */
  pdpUrls?: BriefPdpUrlLink[]
  name: string
  description?: string
  location_request?: string
  brand_requests?: string
  expert_gender?: string
  /** 'industry' | 'use_case' | 'either' — the brand's expert-type intent. */
  expert_type?: string
  expert_interests?: string
  other?: string
  special_requests?: string
  profession_id?: number
  sub_profession_id?: number
  professions?: IBriefProfession[]
  expert_age_min?: number
  expert_age_max?: number
  brand_project_id?: number
  status?: string
  attachments?: BriefAttachment[]
  brief_logo?: string | null
  brief_colors?: string | null
  brief_colors_file?: string | null
  brief_font?: string | null
  brief_font_file?: string | null
  brief_tagline?: string | null
  rmn_brief_logo?: string | null
  rmn_brief_colors?: string | null
  rmn_brief_colors_file?: string | null
  rmn_brief_font?: string | null
  rmn_brief_font_file?: string | null
  rmn_brief_tagline?: string | null
  product_disclaimers?: Record<string, string> | null
}

export interface BriefAttachment {
  id: number
  name: string
  key?: string | null
  bucket?: string | null
  content_type?: string | null
  size?: number | null
  location?: string | null
}

/** A note on a brief's internal thread (GET/POST/PATCH/DELETE /brand-projects/:projectId/briefs/:briefId/notes). */
export interface IBriefNote {
  id: number;
  brand_project_brief_id: number;
  /** The project the note was written from (the thread itself is per brief). */
  brand_project_id: number;
  userid: number | null;
  message: string;
  guest_name: string | null;
  edited: boolean;
  /** Soft-deleted; still listed (the thread is internal-only). */
  deleted: boolean;
  created_at: string;
  updated_at: string;
  user: {
    userid: number;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
    avatar: string | null;
  } | null;
}

export interface ReviewBriefRequest {
  brief_approved: boolean;
  denied_reason?: string;
}
/**
 * A brief's PDP links, one per product, skipping the ones left blank. The two
 * read paths spell the field differently, so always go through here. Briefs
 * written before per-product links existed only carry the single `url`, so fall
 * back to it — both the on-screen view and the PDF read through here.
 */
export const briefPdpUrlRows = (brief: Brief): BriefPdpUrlLink[] =>
  (brief.pdp_urls || brief.pdpUrls || []).map((link) => ({
    ...link,
    product_title: link.product_title || link.product?.title || null,
  }));

export const briefPdpLinks = (brief: Brief): BriefPdpUrlLink[] => {
  const rows = briefPdpUrlRows(brief);
  if (rows.length > 0) {
    return rows.filter((link) => !!link.url);
  }
  if (!brief.url) return [];
  return [
    {
      desireid: 0,
      url: brief.url,
      desires_pdp_url_id: brief.desires_pdp_url_id,
      pdpUrl: brief.pdpUrl,
    },
  ];
};

/** The upload key is `${Date.now()}-${name}`; this is the name the user uploaded. */
export const briefAssetFileName = (url?: string | null): string => {
  if (!url) return '';
  const last = decodeURIComponent(url.split('/').pop() || '');
  return last.replace(/^\d+-/, '');
};

/** TDC-1722: a brief's per-product disclaimers, titled from its PDP rows. */
export const briefDisclaimerRows = (
  brief: Brief
): { desireid: string; title: string; text: string }[] => {
  const titles = new Map(
    briefPdpUrlRows(brief).map((row) => [String(row.desireid), row.product_title || ''])
  );
  return Object.entries(brief.product_disclaimers || {})
    .filter(([, text]) => !!text)
    .map(([desireid, text]) => ({
      desireid,
      title: titles.get(desireid) || '',
      text,
    }));
};

/**
 * True when the brief carries an RMN asset set — only an RMN order's brief
 * does; on a plain brand project those columns stay null.
 */
export const briefHasRmnAssets = (brief: Brief): boolean =>
  BRIEF_RMN_ASSET_KEYS.some((key) => {
    const value = (brief as any)[key];
    return value !== null && value !== undefined;
  });
